import { createContext, useState, useEffect } from "react";
import axios from "axios";
import { encryptData, decryptData } from "../utils/crypto";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState(() => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    if (token && user) {
      try {
        const decryptedUser = decryptData(user);
        return {
          isAuthenticated: true,
          user: decryptedUser,
          token: token,
          loading: true, // Start as loading until token is verified
        };
      } catch (error) {
        return {
          isAuthenticated: false,
          user: null,
          token: null,
          loading: false,
        };
      }
    }
    return { isAuthenticated: false, user: null, token: null, loading: false };
  });

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    const encrypted = encryptData(userData);
    localStorage.setItem("user", encrypted);

    setAuthState({
      isAuthenticated: true,
      user: userData,
      token: token,
      loading: false,
    });
  };

  const updateUserData = (newUserData) => {
    if (!authState.isAuthenticated || !authState.user) return;

    const updatedUser = { ...authState.user, ...newUserData };
    const encrypted = encryptData(updatedUser);
    localStorage.setItem("user", encrypted);

    setAuthState((prev) => ({
      ...prev,
      user: updatedUser,
    }));
  };

  const logout = async () => {
    const token = localStorage.getItem("token");

    try {
      if (token) {
        await axios.delete(
          `${process.env.REACT_APP_API_BASE_URL}/auth/sign-out`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
    } catch (error) {
      console.error("Failed to sign out on the server", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
      });
    }
  };

  useEffect(() => {
    const verifyTokenOnLoad = async () => {
      const token = localStorage.getItem("token");
      if (token && authState.isAuthenticated) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/auth/profile`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          const freshUserData = response.data.data;

          const encrypted = encryptData(freshUserData);
          localStorage.setItem("user", encrypted);

          setAuthState({
            isAuthenticated: true,
            user: freshUserData,
            token: token,
            loading: false,
          });
        } catch (error) {
          console.error("Token verification failed", error);
          logout();
        }
      } else {
        setAuthState((prev) => ({ ...prev, loading: false }));
      }
    };

    verifyTokenOnLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ ...authState, login, logout, updateUserData }}>
      {!authState.loading && children}
    </AuthContext.Provider>
  );
};
